import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, Check, X, Pencil, Trash2, Zap, Search, ChevronDown, CalendarDays, Clock, CheckCircle2, UserCog, Users, Briefcase, Eye, BarChart3 } from 'lucide-react';
import { Sidebar } from '../components/Sidebar';
import { EventCard } from '../components/EventCard';
import { EventForm } from '../components/EventForm';
import { EventPreview } from '../components/EventPreview';
import { BoostModal } from '../components/BoostModal';
import { SiteVisitsCard } from '../components/SiteVisitsCard';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { eventService } from '../services/eventService';
import { isEventActive, getEventDateTime, getEventEndDateTime } from '../lib/helpers';
import { isLongEventFromThirdDay } from '../utils/longEvents';
import { toast } from 'sonner';
import { Timestamp, doc, updateDoc, collection, query, onSnapshot, where } from 'firebase/firestore';
import { db } from "../lib/firebase";
import { CATEGORIES } from '../constants/categories';

export const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('approved');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortMode, setSortMode] = useState(null); // null (padrão) | 'views' | 'viewsUnique'
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [previewEvent, setPreviewEvent] = useState(null);
  const [boostingEvent, setBoostingEvent] = useState(null);
  const [adminBoostEvent, setAdminBoostEvent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [viewerUsers, setViewerUsers] = useState([]);
  const [organizerUsers, setOrganizerUsers] = useState([]);
  const [pendingOrganizers, setPendingOrganizers] = useState(0);

  useEffect(() => {
    const q = query(collection(db, 'users'), where('role', '==', 'viewer'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setViewerUsers(snapshot.docs.map((doc) => doc.data()));
    }, (error) => {
      console.error('[AdminDashboard] Erro no listener de usuários:', error);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'users'), where('role', '==', 'organizer'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setOrganizerUsers(snapshot.docs.map((doc) => doc.data()));
    }, (error) => {
      console.error('[AdminDashboard] Erro no listener de organizadores:', error);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'users'), where('organizerStatus', '==', 'pending'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setPendingOrganizers(snapshot.size);
    }, (error) => {
      console.error('[AdminDashboard] Erro no listener de organizadores pendentes:', error);
    });
    return () => unsubscribe();
  }, []);

  // Quantos itens da lista foram criados desde o dia 1 do mês atual.
  // Ignora documentos sem createdAt (não entram no cálculo, mas continuam no total).
  const getMonthlyGrowth = (docs) => {
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);
    return docs.filter((d) => {
      const createdAt = d.createdAt?.toDate?.();
      return createdAt && createdAt >= startOfMonth;
    }).length;
  };

  useEffect(() => {
    const q = query(collection(db, 'events'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      let eventsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      eventsData.sort((a, b) => {
        if (a.boosted && !b.boosted) return -1;
        if (!a.boosted && b.boosted) return 1;
        if (a.boosted && b.boosted) {
          const aValue = a.boostValue || 0;
          const bValue = b.boostValue || 0;
          if (aValue !== bValue) return bValue - aValue;
        }
        const timeA = a.createdAt?.toMillis() || 0;
        const timeB = b.createdAt?.toMillis() || 0;
        return timeB - timeA;
      });
      setEvents(eventsData);
      setLoading(false);
    }, (error) => {
      console.error('[AdminDashboard] Erro no listener de eventos:', error);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Auto-open modal when URL contains eventId (direct link / deep link)
  useEffect(() => {
    if (!eventId || loading) return;
    const event = events.find(e => e.id === eventId);
    if (event) {
      setPreviewEvent(event);
    } else {
      navigate('/admin', { replace: true });
    }
  }, [eventId, loading, events, navigate]);

  // Auto-close modal when browser back button removes eventId from URL
  useEffect(() => {
    if (!eventId) setPreviewEvent(null);
  }, [eventId]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleOpenPreview = (event) => {
    setPreviewEvent(event);
    navigate(`/admin/event/${event.id}`);
  };

  const handleClosePreview = () => {
    setPreviewEvent(null);
    navigate('/admin', { replace: true });
  };


  const handleCreateEvent = async (formData, imageFile) => {
    // Date/time combination (embedding the hour into the Timestamp) is handled
    // by eventService, which mirrors the Flutter app's data format.
    const eventData = {
      ...formData,
      createdBy: user.uid
    };

    // Admin cria eventos com status "approved" automaticamente
    const result = await eventService.createEvent(eventData, imageFile, 'approved');
    if (result.success) {
      toast.success('Evento criado e aprovado automaticamente!');
      setShowForm(false);
    } else {
      toast.error('Erro ao criar evento');
    }
  };

  const handleUpdateEvent = async (formData, imageFile) => {
    // Date/time combination (embedding the hour into the Timestamp) is handled
    // by eventService, which mirrors the Flutter app's data format.
    const eventData = { ...formData };

    const result = await eventService.updateEvent(editingEvent.id, eventData, imageFile);
    if (result.success) {
      toast.success('Evento atualizado com sucesso!');
      setEditingEvent(null);
    } else {
      toast.error('Erro ao atualizar evento');
    }
  };

  const handleApprove = async (eventId) => {
    const result = await eventService.updateEventStatus(eventId, 'approved');
    if (result.success) {
      toast.success('Evento aprovado!');
    } else {
      toast.error('Erro ao aprovar evento');
    }
  };

  const handleReject = async (eventId) => {
    if (window.confirm('Tem certeza que deseja rejeitar este evento?')) {
      const result = await eventService.updateEventStatus(eventId, 'rejected');
      if (result.success) {
        toast.success('Evento rejeitado!');
      } else {
        toast.error('Erro ao rejeitar evento');
      }
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (window.confirm('Tem certeza que deseja excluir este evento? Esta ação não pode ser desfeita.')) {
      const result = await eventService.deleteEvent(eventId);
      if (result.success) {
        toast.success('Evento excluído com sucesso!');
      } else {
        toast.error('Erro ao excluir evento');
      }
    }
  };

  // Boost completion handler - just reload events
  const handleBoostComplete = () => {
    setBoostingEvent(null);
  };

  // Toggle: clicar no modo já ativo volta pra ordenação padrão (null).
  // Só um modo de ordenação especial fica ativo por vez.
  const toggleSortMode = (mode) => {
    setSortMode((prev) => (prev === mode ? null : mode));
  };

  const normalizeText = (text = '') =>
    text.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

  const getFilteredEvents = () => {
    let filtered;

    if (filter === 'approved') {
      // Aprovados: mesma regra da aba "Ativos" do Organizer — aprovados e
      // ainda não encerrados (now <= endDate + 1min).
      filtered = events.filter(e => e.status === 'approved' && isEventActive(e));
    } else if (filter === 'boosted') {
      const now = new Date();
      // Impulsionados: aprovados, com boost vigente, e ainda não encerrados.
      filtered = events.filter(e =>
        e.status === 'approved' &&
        e.boosted === true &&
        e.boostEnd?.toDate?.() > now &&
        isEventActive(e)
      );
    } else if (filter === 'all') {
      // Todos: tudo exceto rejeitados, sem filtro de horário — o Admin
      // precisa conseguir encontrar/gerenciar qualquer evento, mesmo já
      // encerrado (diferente do Organizer, que tem uma aba dedicada
      // "Eventos Encerrados"; o Admin não tem equivalente).
      filtered = events.filter(e => e.status !== 'rejected');
    } else {
      // Pendentes / Rejeitados: filtro só por status, sem filtro de horário.
      filtered = events.filter(e => e.status === filter);
    }

    if (categoryFilter) {
      filtered = filtered.filter(e => e.category === categoryFilter);
    }

    if (debouncedQuery.trim()) {
      const q = normalizeText(debouncedQuery);
      filtered = filtered.filter(e =>
        normalizeText(e.title).includes(q) ||
        normalizeText(e.description).includes(q) ||
        normalizeText(e.location).includes(q)
      );
    }

    // Ordenação especial (toggle) por views, aplicada em cima do filtro/busca
    // acima — quando desligada (sortMode === null), mantém a ordenação padrão
    // por data que já existia.
    if (sortMode === 'views') {
      return filtered.sort((a, b) => (b.views || 0) - (a.views || 0));
    }
    if (sortMode === 'viewsUnique') {
      return filtered.sort((a, b) => (b.viewsUnique || 0) - (a.viewsUnique || 0));
    }

    const byDate = filtered.sort((a, b) => {
      const aDate = a.date?.toDate?.() || new Date(a.date);
      const bDate = b.date?.toDate?.() || new Date(b.date);
      return aDate - bDate;
    });

    // Aprovados: evento longo (mesma regra do "Em cartaz" do site) já no 3º
    // dia ou depois vai pro fim — senão fica sempre no topo, por ter começado
    // antes. Os dois grupos mantêm a ordem por data acima.
    if (filter !== 'approved') return byDate;
    const regular = [];
    const longFromThirdDay = [];
    for (const e of byDate) {
      const range = { _startDate: getEventDateTime(e), _endDate: getEventEndDateTime(e) };
      (isLongEventFromThirdDay(range) ? longFromThirdDay : regular).push(e);
    }
    return [...regular, ...longFromThirdDay];
  };

  const getEventStats = () => {
    return {
      total: events.length,
      pending: events.filter(e => e.status === 'pending').length,
      approved: events.filter(e => e.status === 'approved').length,
      rejected: events.filter(e => e.status === 'rejected').length
    };
  };

  const filteredEvents = getFilteredEvents();
  const stats = getEventStats();
  const eventsGrowth = getMonthlyGrowth(events);
  const usersGrowth = getMonthlyGrowth(viewerUsers);
  const organizersGrowth = getMonthlyGrowth(organizerUsers);

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      
      <main className="flex-1 p-4 pt-16 md:p-8 md:ml-72">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl xl:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Painel Administrativo</h1>
              <p className="text-muted-foreground">Gerencie e aprove eventos</p>
            </div>
            <Button onClick={() => setShowForm(true)} className="gap-2 shrink-0" data-testid="admin-create-event-btn">
              <Plus className="w-4 h-4" />
              Criar Evento
            </Button>
          </div>

          {/* 2 por linha no celular (visitas ocupa a linha inteira), 4 + 3 no
              notebook, 7 numa linha só em tela larga */}
          <div className="grid grid-cols-2 lg:grid-cols-4 2xl:grid-cols-7 gap-3">
            <DashboardStatCard
              icon={CalendarDays}
              label="Eventos Totais"
              subtitle={`+${eventsGrowth} este mês`}
              value={stats.total}
              colorClass="text-foreground bg-muted"
              testId="admin-stat-total"
            />
            <DashboardStatCard
              icon={Clock}
              label="Eventos Pendentes"
              value={stats.pending}
              colorClass="text-yellow-600 bg-yellow-100 dark:bg-yellow-950 dark:text-yellow-300"
              testId="admin-stat-pending"
            />
            <DashboardStatCard
              icon={CheckCircle2}
              label="Eventos Aprovados"
              value={stats.approved}
              colorClass="text-green-600 bg-green-100 dark:bg-green-950 dark:text-green-300"
              testId="admin-stat-approved"
            />
            <DashboardStatCard
              icon={Briefcase}
              label="Organizadores Totais"
              subtitle={`+${organizersGrowth} este mês`}
              value={organizerUsers.length}
              colorClass="text-indigo-600 bg-indigo-100 dark:bg-indigo-950 dark:text-indigo-300"
              testId="admin-stat-total-organizers"
            />
            <DashboardStatCard
              icon={UserCog}
              label="Organizadores Pendentes"
              value={pendingOrganizers}
              colorClass="text-purple-600 bg-purple-100 dark:bg-purple-950 dark:text-purple-300"
              testId="admin-stat-pending-organizers"
            />
            <DashboardStatCard
              icon={Users}
              label="Usuários"
              subtitle={`+${usersGrowth} este mês`}
              value={viewerUsers.length}
              colorClass="text-blue-600 bg-blue-100 dark:bg-blue-950 dark:text-blue-300"
              testId="admin-stat-users"
            />
            <SiteVisitsCard className="col-span-2 lg:col-span-1" />
          </div>

          <div className="bg-card border border-border rounded-xl p-6">
            <div className="flex flex-wrap gap-2 items-center">
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
                data-testid="filter-all"
              >
                Todos
              </Button>
              <Button
                variant={filter === 'pending' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('pending')}
                data-testid="filter-pending"
              >
                Pendentes
              </Button>
              <Button
                variant={filter === 'approved' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('approved')}
                data-testid="filter-approved"
              >
                Aprovados
              </Button>
              <Button
                variant={filter === 'rejected' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('rejected')}
                data-testid="filter-rejected"
              >
                Rejeitados
              </Button>
              <Button
                variant={filter === 'boosted' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('boosted')}
              >
                Impulsionados
              </Button>
              <div className="w-px h-6 bg-border mx-1 shrink-0" aria-hidden="true" />
              <Button
                variant={sortMode === 'views' ? 'default' : 'outline'}
                size="sm"
                className="gap-1.5"
                onClick={() => toggleSortMode('views')}
                data-testid="sort-most-viewed"
              >
                <BarChart3 className="w-4 h-4" />
                Mais vistos
              </Button>
              <Button
                variant={sortMode === 'viewsUnique' ? 'default' : 'outline'}
                size="sm"
                className="gap-1.5"
                onClick={() => toggleSortMode('viewsUnique')}
                data-testid="sort-most-unique"
              >
                <Eye className="w-4 h-4" />
                Mais únicas
              </Button>
              <div className="relative shrink-0">
                <select
                  value={categoryFilter}
                  onChange={(e) => setCategoryFilter(e.target.value)}
                  className="appearance-none w-40 h-8 pl-3 pr-8 border border-input shadow-sm rounded-md bg-background text-xs font-medium cursor-pointer focus:outline-none focus:ring-1 focus:ring-ring transition-colors hover:bg-accent hover:text-accent-foreground"
                  data-testid="category-filter"
                >
                  <option value="">Todas as categorias</option>
                  {CATEGORIES.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              </div>
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  placeholder="Pesquisar por título, descrição ou local..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-9 pl-10 pr-3 border border-border rounded-md bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all hover:border-muted-foreground/40"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">Carregando eventos...</p>
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="text-center py-12 bg-card border border-border rounded-xl">
              <p className="text-muted-foreground font-medium">Nenhum evento encontrado</p>
              <p className="text-sm text-muted-foreground mt-1">Tente ajustar os filtros ou pesquisar outro termo.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-6" data-testid="admin-events-grid">
              {filteredEvents.map((event) => (
                <EventCard
                  key={event.id}
                  event={event}
                  onPreview={() => handleOpenPreview(event)}
                  actions={
                    <div className="space-y-2">
                      {event.status === 'pending' && (
                        <div className="flex flex-wrap gap-2">
                          <Button
                            size="sm"
                            className="flex-1 min-w-[90px] gap-2 bg-green-600 hover:bg-green-700"
                            onClick={() => handleApprove(event.id)}
                            data-testid={`approve-event-${event.id}`}
                          >
                            <Check className="w-4 h-4" />
                            Aprovar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 min-w-[90px] gap-2 text-destructive hover:bg-destructive/10"
                            onClick={() => handleReject(event.id)}
                            data-testid={`reject-event-${event.id}`}
                          >
                            <X className="w-4 h-4" />
                            Rejeitar
                          </Button>
                        </div>
                      )}
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 min-w-[90px] gap-2"
                          onClick={() => setBoostingEvent(event)}
                          data-testid={`boost-event-${event.id}`}
                        >
                          <Zap className="w-4 h-4" />
                          Impulsionar
                        </Button>
                        {user?.email === "augusto.costa00@gmail.com" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 gap-2 bg-orange-500 text-white hover:bg-orange-600"
                            onClick={() => setAdminBoostEvent(event)}
                          >
                            <Zap className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 min-w-[90px] gap-2"
                          onClick={() => setEditingEvent(event)}
                          data-testid={`edit-event-${event.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 min-w-[90px] gap-2 text-destructive hover:bg-destructive/10"
                          onClick={() => handleDeleteEvent(event.id)}
                          data-testid={`delete-event-${event.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                          Excluir
                        </Button>
                      </div>
                    </div>
                  }
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {showForm && (
        <EventForm
          onClose={() => setShowForm(false)}
          onSubmit={handleCreateEvent}
          isAdmin={true}
        />
      )}

      {editingEvent && (
        <EventForm
          initialData={editingEvent}
          onClose={() => setEditingEvent(null)}
          onSubmit={handleUpdateEvent}
          isAdmin={true}
        />
      )}

      {previewEvent && (
        <EventPreview
          event={previewEvent}
          onClose={handleClosePreview}
          onBoost={setBoostingEvent}
        />
      )}

      {boostingEvent && (
        <BoostModal
          event={boostingEvent}
          onClose={() => setBoostingEvent(null)}
          onBoostComplete={handleBoostComplete}
        />
      )}
      {adminBoostEvent && (
  <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
    <div className="bg-white p-6 rounded-xl space-y-4">
      <h2 className="text-lg font-bold">Boost Admin</h2>

      <div className="flex gap-2">
        {[1, 3, 7].map((dias) => (
          <Button
            key={dias}
            onClick={async () => {
              const now = new Date();
              const end = new Date();
              end.setDate(now.getDate() + dias);

              await updateDoc(doc(db, "events", adminBoostEvent.id), {
              boosted: true,
              boostStart: Timestamp.fromDate(now),
              boostEnd: Timestamp.fromDate(end),
              boostType: "admin_manual",
              boostStatus: "active",
              boostScore: 999
            });

              toast.success(`Evento impulsionado por ${dias} dias`);
              setAdminBoostEvent(null);
            }}
          >
            {dias} dia{dias > 1 ? 's' : ''}
          </Button>
        ))}
      </div>

      <Button variant="outline" onClick={() => setAdminBoostEvent(null)}>
        Cancelar
      </Button>
    </div>
  </div>
)}
    </div>
  );
};

const DashboardStatCard = ({ icon: Icon, label, value, subtitle, colorClass, testId }) => {
  return (
    // Cabeçalho com altura mínima de 2 linhas: o número fica na mesma altura
    // em todos os cards, com título de 1 ou 2 linhas (mesmo padrão do SiteVisitsCard).
    <div className="h-full bg-card border border-border rounded-xl p-3 sm:p-4 2xl:p-3 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2 min-h-10">
        <p className="text-xs sm:text-sm font-medium text-muted-foreground leading-tight">{label}</p>
        <div className={`shrink-0 p-2 rounded-lg ${colorClass}`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div>
        <p className="text-2xl font-bold tracking-tight" data-testid={testId}>{value}</p>
        {subtitle && <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>}
      </div>
    </div>
  );
};
